import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { getSettings, createPaymentIntent, createOrder } from '../services/api';
import { toast } from '../hooks/use-toast';

const CheckoutForm = ({ cart, clientSecret, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    full_name: '',
    email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    phone: ''
  });

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal > 69 ? 0 : 5.50;
  const total = subtotal + shipping;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      // Confirm the payment
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/order-confirmation',
        },
        redirect: 'if_required'
      });

      if (error) {
        toast({
          title: 'Payment failed',
          description: error.message,
          variant: 'destructive'
        });
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Create order in database
        const orderData = {
          customer_email: shippingInfo.email,
          items: cart.map(item => ({
            product_id: item.id,
            product_name: item.name,
            quantity: item.quantity,
            price: item.price,
            size: item.selectedSize,
            color: item.selectedColor,
            image: item.images?.[0] || item.image
          })),
          subtotal,
          shipping,
          total,
          payment_intent_id: paymentIntent.id,
          shipping_address: shippingInfo
        };

        const order = await createOrder(orderData);
        onSuccess(order);
      }
    } catch (err) {
      console.error('Payment error:', err);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-light">Shipping Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={shippingInfo.full_name}
                onChange={(e) => setShippingInfo({...shippingInfo, full_name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={shippingInfo.email}
                onChange={(e) => setShippingInfo({...shippingInfo, email: e.target.value})}
                required
              />
            </div>
          </div>
          <div>
            <Label>Address Line 1 *</Label>
            <Input
              value={shippingInfo.address_line1}
              onChange={(e) => setShippingInfo({...shippingInfo, address_line1: e.target.value})}
              required
            />
          </div>
          <div>
            <Label>Address Line 2</Label>
            <Input
              value={shippingInfo.address_line2}
              onChange={(e) => setShippingInfo({...shippingInfo, address_line2: e.target.value})}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>City *</Label>
              <Input
                value={shippingInfo.city}
                onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Postal Code *</Label>
              <Input
                value={shippingInfo.postal_code}
                onChange={(e) => setShippingInfo({...shippingInfo, postal_code: e.target.value})}
                required
              />
            </div>
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              type="tel"
              value={shippingInfo.phone}
              onChange={(e) => setShippingInfo({...shippingInfo, phone: e.target.value})}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-light">Payment Details</CardTitle>
        </CardHeader>
        <CardContent>
          <PaymentElement />
        </CardContent>
      </Card>

      <Button
        type="submit"
        disabled={!stripe || loading}
        size="lg"
        className="w-full bg-neutral-900 hover:bg-neutral-800 h-14 text-lg"
      >
        {loading ? 'Processing...' : `Pay £${total.toFixed(2)}`}
      </Button>
    </form>
  );
};

const Checkout = ({ cart, onClearCart }) => {
  const navigate = useNavigate();
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeCheckout = async () => {
    try {
      // Get Stripe publishable key
      const settings = await getSettings();
      if (!settings?.stripe_publishable_key) {
        toast({
          title: 'Error',
          description: 'Payment system not configured',
          variant: 'destructive'
        });
        navigate('/cart');
        return;
      }

      const stripe = await loadStripe(settings.stripe_publishable_key);
      setStripePromise(stripe);

      // Create payment intent
      const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const shipping = subtotal > 69 ? 0 : 5.50;
      const total = subtotal + shipping;

      const { clientSecret } = await createPaymentIntent(total);
      setClientSecret(clientSecret);
    } catch (error) {
      console.error('Checkout initialization error:', error);
      toast({
        title: 'Error',
        description: 'Failed to initialize checkout',
        variant: 'destructive'
      });
      navigate('/cart');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = (order) => {
    onClearCart();
    navigate(`/order-confirmation/${order.id}`, { state: { order } });
  };

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal > 69 ? 0 : 5.50;
  const total = subtotal + shipping;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-neutral-600">Initializing secure checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-light text-neutral-900 mb-8">Checkout</h1>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Checkout Form */}
        <div className="md:col-span-2">
          {stripePromise && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CheckoutForm
                cart={cart}
                clientSecret={clientSecret}
                onSuccess={handleSuccess}
              />
            </Elements>
          )}
        </div>

        {/* Order Summary */}
        <div>
          <Card className="sticky top-24">
            <CardHeader>
              <CardTitle className="font-light">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {cart.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span>{item.name} x {item.quantity}</span>
                  <span>£{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <Separator />
              <div className="flex justify-between text-neutral-600">
                <span>Subtotal</span>
                <span>£{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-neutral-600">
                <span>Shipping</span>
                <span>{shipping === 0 ? 'FREE' : `£${shipping.toFixed(2)}`}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-xl font-light">
                <span>Total</span>
                <span>£{total.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
